import { TextField } from "@mui/material";
import { InputAdornment } from "@mui/material";
import { get } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useController, useFormContext, FieldValues } from "react-hook-form";
import { View } from "react-native";

import config from "../../config";
import { colors } from "../../../theme";
import { ErrorHelperText } from "../TextInput";
import { TextInputProps } from "./types";
import { countryCode } from "../AddressAutoComplete/utils";
import { Icon } from "../TextInput";
import { placeToFormData } from "../AddressAutoComplete/placeToFormData";

const scriptSrc = `https://maps.googleapis.com/maps/api/js?key=${config.googlePlacesApiKeyUnrestricted}&libraries=places`;

interface Places {
  Autocomplete: typeof google.maps.places.Autocomplete;
}

function getPlaces(): Promise<Places> {
  const getClass = () => get(window, ["google", "maps", "places"]) as unknown as Places | undefined;

  return new Promise((resolve) => {
    const tag = document.querySelector(`script[src="${scriptSrc}"]`);
    if (tag) {
      const autocompleteClass = getClass();
      if (autocompleteClass) {
        resolve(autocompleteClass);
        return;
      }
    }

    const scriptTag = document.createElement("script");
    scriptTag.src = scriptSrc;
    scriptTag.async = true;
    scriptTag.addEventListener("load", () => {
      const autocompleteClass = getClass();
      if (autocompleteClass) resolve(autocompleteClass);
    });
    document.head.appendChild(scriptTag);
  });
}

const styles = (hasError: boolean) => ({
  ".MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? colors.red.dark : colors.sky.dark,
    borderRadius: "8px",
    borderWidth: hasError ? "2px" : "1px",
  },
  "& .MuiInputLabel-root": {
    top: "-2px",
  },
});

function AutocompleteInput<T extends FieldValues>({
  name,
  label,
  autoComplete,
  control,
  rules,
  countryField,
  filterReverseGeocodingByTypes,
  onBlur,
  setLocationSelected,
}: TextInputProps<T>) {
  const [places, setPlaces] = useState<Places | undefined>();
  const [input, setInput] = useState<HTMLInputElement | undefined>();
  const { watch } = useFormContext<T>();

  const country = countryCode(watch, countryField);
  const componentRestrictions = { country };

  const autocompleteLibRef = useRef<google.maps.places.Autocomplete>();

  const {
    field: { ref: formRef, ...field },
    fieldState: { error },
  } = useController({ control, name, rules });
  const hasError = !!error?.message;

  const autocompleteRef = useCallback((node: HTMLInputElement | null) => {
    if (node !== null && input === undefined) {
      setInput(node);
    }
  }, []);

  useEffect(() => {
    getPlaces().then(setPlaces);
  }, []);

  useEffect(() => {
    if (input && places) {
      const autocomplete = new places.Autocomplete(input, {
        componentRestrictions,
        types: filterReverseGeocodingByTypes || ["address"],
      });
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const { city, state } = placeToFormData(place);
        field.onChange(`${city ? `${city},` : ""} ${state}` || "");
        setLocationSelected && setLocationSelected(true);
      });

      autocompleteLibRef.current = autocomplete;

      return () => {
        autocompleteLibRef.current = undefined;
        autocomplete.unbindAll();
      };
    }
  }, [input, places]);

  useEffect(() => {
    autocompleteLibRef.current?.setOptions({ componentRestrictions });
  }, [country]);

  function ref(node: HTMLInputElement) {
    autocompleteRef(node);
    formRef(node);
  }

  return (
    <View className="mt-2">
      <TextField
        sx={styles(hasError)}
        inputProps={{
          style: { paddingTop: 13.5, paddingBottom: 13.5 },
          "data-1p-ignore": true,
        }}
        InputProps={{
          placeholder: "Location (City or Zip)",
          endAdornment: (
            <InputAdornment position="end" style={{ marginRight: 24 }}>
              <Icon icon="map-pin" />
            </InputAdornment>
          ),
        }}
        inputRef={ref}
        {...field}
        {...{ autoComplete, label, onBlur }}
        onChange={(e) => {
          field.onChange(e);
          setLocationSelected && setLocationSelected(false);
        }}
      />
      {hasError && <ErrorHelperText error={error}>{error?.message}</ErrorHelperText>}
    </View>
  );
}

export default AutocompleteInput;
